import { format } from 'date-fns';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Cookies } from 'react-cookie';
import { useHistory, useLocation } from 'react-router';
import Swal from 'sweetalert2';
import { v4 } from 'uuid';
import * as Yup from 'yup';

import { useSettings } from '~/hooks/settings';
import App from '~/layouts/App';
import getOrderDetails from '~/utils/getOrderDetails2';
import getValidationErrors from '~/utils/getValidationErrors';

import { useCart } from '~/hooks/cart';
import { useAnalytics } from '~/hooks/analytics';
import IAddress from '~/interfaces/IAddress';
import IOrder from '~/interfaces/IOrder';
import { DB, Realtime } from '~/services/firebase';

import { useAppAuth } from '~/hooks/appAuth';
import { useProducts } from '~/hooks/products';
import ICartProduct from '~/interfaces/ICartProduct';
import IClient from '~/interfaces/IClient';
import IDistance from '~/interfaces/IDistance';
import IReport from '~/interfaces/IReport';
import ITableOrders from '~/interfaces/ITableOrders';
import { bff } from '~/services/bff';
import { registerOrderInTable } from '~/utils/checkout/registerOrderInTable';
import getAddressFormatted from '~/utils/getAddressFormatted';
import getCurrencyFormatted from '~/utils/getCurrencyFormatted';
import { ORDER_TYPES_CONSTS, PAYMENT_METHODS } from '~/utils/getEnums';

import { notifyTableOrder } from '~/utils/checkout/notifyTableOrder';

interface FormData {
  paymentMethod: string;
  thing: string;
  name: string;
  whatsapp: string;
}

const cookies = new Cookies();
const {
  REACT_APP_APP_NAME,
  REACT_APP_USE_API_PRODUCTS,
  REACT_APP_SIMPLE_ADDRESS,
  REACT_APP_FIREBASE_PROJECT,
} = process.env;

const Checkout: React.FC = () => {
  const { search } = useLocation();
  const history = useHistory();
  const { user } = useAppAuth();
  const { logPurchase, logEvent } = useAnalytics();
  const { company, loadCompany, table } = useSettings();
  const {
    products: productsFromContext,
    loadProducts,
    loadProductsHeroku,
  } = useProducts();
  const {
    products,
    deliveryMethod,
    address,
    clearCart,
    distances: distances_ctx,
    updateDistances,
    coupon,
    fidelitDiscount,
    setFidelitDiscount,
  } = useCart();

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({} as any);
  const [yearMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [day] = useState(format(new Date(), 'dd'));
  const [distances, setDistances] = useState(distances_ctx);
  // const [remember, setRemember] = useState(() => {
  //   const tmp = cookies.get(`@${REACT_APP_APP_NAME}:client`);

  //   if (tmp) return true;

  //   return false;
  // });

  useEffect(() => {
    async function loadDistances() {
      const q = query(
        collection(DB, 'distances'),
        where('company_id', '==', company.uid),
      );
      const querySnapshot = await getDocs(q);

      const items: IDistance[] = [];

      querySnapshot.docs.forEach(item => {
        const parseItem = {
          ...item.data(),
          uid: item.id,
        } as IDistance;

        items.push(parseItem);
      });

      items.sort((a, b) => (a.from > b.from ? 1 : b.from > a.from ? -1 : 0));

      setDistances(items);
      updateDistances(items);
    }

    // if (distances_ctx.length === 0 && company && company.uid)
    if (company.uid) loadDistances();
  }, [company, updateDistances]);

  const [data, setData] = useState<FormData>(() => {
    const tmp = cookies.get(`@${REACT_APP_APP_NAME}:client`);

    if (tmp) {
      return {
        ...tmp,
        paymentMethod: '0',
      } as FormData;
    }

    return {
      paymentMethod: '0',
    } as FormData;
  });

  const parseProducts = useMemo(() => {
    const tmp: ICartProduct[] = [];

    products.forEach(item => {
      let total = 0;

      if (item.pizza_type) {
        item.complements.forEach(element => {
          const itemValue = parseFloat(element.item.value);
          if (itemValue > total) total = itemValue;
        });
      } else {
        item.complements.forEach(element => {
          total += element.quantity * parseFloat(element.item.value);
        });
      }

      tmp.push({
        ...item,
        value: `${parseFloat(item.value) + total}`,
      });
    });

    return tmp;
  }, [products]);

  const shippingCost = useMemo(() => {
    if (!company.uid || deliveryMethod === '0' || company.free_delivery === '1')
      return 0;

    if (distances.length === 0) return parseFloat(company.shipping_cost);

    if (REACT_APP_SIMPLE_ADDRESS) {
      const findDistance = distances.find(
        find => find.district === address.neighbourhood,
      );
      if (findDistance) {
        return findDistance?.value;
      }
    }

    if (address.distance) {
      const distanceKm = address.distance?.value / 1000;

      const findDistance = distances.find(
        find => distanceKm > find.from && distanceKm <= find.to,
      );

      if (findDistance && findDistance.value) return findDistance.value;
    }

    return parseFloat(company.shipping_cost);
  }, [company, deliveryMethod, address, distances]);

  const subtotal = useMemo(() => {
    let total = 0;

    parseProducts.forEach(item => {
      total += parseFloat(`${item.quantity}`) * parseFloat(item.value);
    });

    return total;
  }, [parseProducts]);

  const couponDiscount = useMemo(() => {
    if (!coupon || !coupon.uid) return 0;

    const discountValue =
      coupon.type === 0
        ? parseFloat(((subtotal * coupon.value) / 100).toFixed(2))
        : coupon.value;

    return discountValue;
  }, [subtotal, coupon]);

  const total = useMemo(() => {
    let sum = 0;

    if (!company.uid) return sum;

    sum = subtotal;

    if (deliveryMethod === '1') sum += shippingCost;

    if (
      company.add_card &&
      company.add_card > 0 &&
      data.paymentMethod === '2'
    ) {
      if (company.add_card_type === '1') {
        const addCard = (subtotal * company.add_card) / 100;
        sum += addCard;
      } else {
        sum += company.add_card;
      }
    }

    if (
      company.add_card2 &&
      company.add_card2 > 0 &&
      data.paymentMethod === '1'
    ) {
      if (company.add_card2_type === '1') {
        const addCard = (subtotal * company.add_card2) / 100;
        sum += addCard;
      } else {
        sum += company.add_card2;
      }
    }

    return sum - couponDiscount - fidelitDiscount;
  }, [
    subtotal,
    company,
    shippingCost,
    deliveryMethod,
    data.paymentMethod,
    couponDiscount,
    fidelitDiscount,
  ]);

  const fidelitAlert = useMemo(() => {
    if (company.fidelit && company.fidelit_min_value) {
      if (subtotal < company.fidelit_min_value) {
        return {
          message: `Atenção! Essa compra não contará como fidelidade, pois o valor é inferior ao mínimo de ${getCurrencyFormatted(
            company.fidelit_min_value,
          )}.`,
          value: true,
        };
      }
    }

    return {
      message: '',
      value: false,
    };
  }, [company, subtotal]);

  const messageAddCard = useMemo(() => {
    let addCard = '0';

    if (data.paymentMethod === '2') {
      addCard =
        company.add_card_type === '1'
          ? `${company.add_card}%`
          : getCurrencyFormatted(company.add_card);

      return `Pagamentos no cartão de crédito tem acréscimo de ${addCard}. O valor do seu pedido com o acréscimo será de ${getCurrencyFormatted(
        total,
      )}`;
    }

    addCard =
      company.add_card2_type === '1'
        ? `${company.add_card2}%`
        : getCurrencyFormatted(company.add_card2);

    return `Pagamentos no cartão de débito tem acréscimo de ${addCard}. O valor do seu pedido com o acréscimo será de ${getCurrencyFormatted(
      total,
    )}`;
  }, [company, total, data]);

  const updateReport = useCallback(async () => {
    const companiesRef = collection(DB, 'reports');
    const q = query(companiesRef, where('company_id', '==', company.uid));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const item = {
        ...querySnapshot.docs[0].data(),
        uid: querySnapshot.docs[0].id,
      } as IReport;

      let orders_quantity = 1;
      let sales_total = 0;

      if (item.data[yearMonth] && item.data[yearMonth][day]) {
        orders_quantity += item.data[yearMonth][day].orders_quantity;
        sales_total += item.data[yearMonth][day].sales_total;
      }

      await updateDoc(doc(DB, 'reports', item.uid), {
        [`data.${yearMonth}.${day}`]: {
          orders_quantity,
          sales_total,
        },
      });
    }
  }, [company, yearMonth, day]);

  const notify = useCallback(
    (name: string, orderTotal: number, order_id: string) => {
      const notification = {
        type: '0',
        description: `${name} realizou um novo pedido, no valor de ${getCurrencyFormatted(
          orderTotal,
        )}`,
        link: `/orders/details/${order_id}`,
        company_id: company.uid,
        user_id: company.user_id,
        created_at: new Date(),
      };

      const notificationsRef = Realtime.ref(`notifications/${company.user_id}`);
      const newNotificationRef = notificationsRef.push();
      newNotificationRef.set(notification);
    },
    [company],
  );

  const orderStatusRealtime = useCallback(
    (order_id: string) => {
      const orderStatus = {
        order_id,
        status: '0',
      };

      const ref = Realtime.ref(`orders/${user.uid}`);
      const newNotificationRef = ref.push();
      newNotificationRef.set(orderStatus);
    },
    [user],
  );

  const updateInventoryOfProductsSold = useCallback(async () => {
    const batch = writeBatch(DB);

    products.forEach(product => {
      const find = productsFromContext.find(item => item.uid === product.uid);

      if (find && find.stok) {
        const productRef = doc(DB, 'products', find.uid);
        batch.update(productRef, { stok: find.stok - product.quantity });
      }
    });

    await batch.commit();
  }, [products, productsFromContext]);

  const handleRegisterOrderInTable = useCallback(
    async (parseData: any) => {
      const q = query(
        collection(DB, 'table_orders'),
        where('table_id', '==', table.uid),
        where('status', '==', 1),
        // limit(1),
      );

      const findTable = await getDocs(q);

      if (findTable && findTable.docs.length > 0) {
        const currentTable = findTable.docs[0].data() as ITableOrders;

        const ordersList = [...currentTable.orders, parseData];

        await updateDoc(doc(DB, 'table_orders', currentTable.uid), {
          orders: ordersList,
          total: currentTable.total + total,
          total_formmated: getCurrencyFormatted(currentTable.total + total),
        });
      } else {
        const tableOrder = {
          uid: v4(),
          table_id: table.uid,
          table_name: table.name,
          user_id: table.user_id,
          company_id: table.company_id,
          orders: [parseData],
          status: 1,
          created_at: new Date(),
          total,
          total_formmated: getCurrencyFormatted(total),
        } as ITableOrders;

        await setDoc(doc(DB, 'table_orders', tableOrder.uid), tableOrder);
      }
    },
    [table, total],
  );

  const handleOrderTable = useCallback(async () => {
    const uid = v4();
    const code = `${uid.substr(0, 3)}${uid.substr(uid.length - 3, 3)}`;

    const carrinhoDeProdutos = parseProducts.map(product => ({
      ...product,
      product_id: product.uid,
      id: v4(),
    }));

    const parseData: any = {
      products: carrinhoDeProdutos,
      client_id: user.uid || '',
      shippingCost: 0,
      subtotal,
      subtotal_formatted: getCurrencyFormatted(subtotal),
      total,
      additionValue: 0,
      address: {},
      company_id: company.uid,
      company_name: company.fantasy,
      company_user_id: company.user_id,
      created_at: new Date(),
      code,
      status: '0',
      pix_file: '',
      name: user.name,
      whatsapp: '',
      paymentMethod: '',
      thing: '',
      uid,
      order_type: ORDER_TYPES_CONSTS.TABLE,
      table,
    };

    await registerOrderInTable({
      parseData,
      table,
      waiter_name: '',
    });

    notifyTableOrder({
      table,
      company,
      order: parseData,
    });

    const success_message = 'Seu pedido foi realizado!';

    Swal.fire({
      title: 'Parabéns!',
      text: success_message,
      icon: 'success',
      confirmButtonColor: company.app_color,
    });

    clearCart();

    history.push(`/${company.app_domain}?table=${table.uid}`);

    setLoading(false);

    // orderStatusRealtime(uid);
    // await updateInventoryOfProductsSold(parseProducts);
  }, [
    company,
    table,
    parseProducts,
    subtotal,
    total,
    user,
    history,
    clearCart,
  ]);

  const handleSubmit = useCallback(async () => {
    try {
      setErrors({});

      if (!company.opened) {
        setLoading(false);
        alert('Que pena! Estamos fechados : (');
        return;
      }

      if (table.uid) {
        await handleOrderTable();
        return;
      }

      const uid = v4();
      const code = `${uid.substr(0, 3)}${uid.substr(uid.length - 3, 3)}`;

      // Buscar dados do cliente para obter o order_count atual
      const docClient = await getDoc(doc(DB, 'clients', user.uid));
      const clientData = docClient.data() as IClient;
      const currentOrderCount = clientData.order_count || 0;

      const parseData = {
        ...data,
        thing: data.paymentMethod === '0' ? data.thing : '',
        paymentMethod: '',
        orderPayments: [
          {
            payment_method_id: data.paymentMethod,
            value: total,
            value_formatted: getCurrencyFormatted(total),
          },
        ],
        products: parseProducts,
        client_id: user.uid,
        shippingCost: deliveryMethod === '1' ? shippingCost : 0,
        subtotal,
        total,
        address: deliveryMethod === '1' ? address : ({} as IAddress),
        company_name: company.fantasy,
        company_id: company.uid,
        company_user_id: company.user_id,
        created_at: new Date(),
        code,
        status: '0',
        pix_file: '',
        table,
        order_type: deliveryMethod,
        coupon: coupon.uid ? coupon : {},
        fidelitDiscount,
        client_order_count: currentOrderCount + 1,
        uid,
      } as IOrder;

      const schema = Yup.object().shape(
        company.thing_required === '1' && data.paymentMethod === '0'
          ? {
              name: Yup.string().required('O campo nome é obrigatório'),
              whatsapp: Yup.string().required('O campo whatsapp é obrigatório'),
              thing: Yup.string().required('O campo troco é obrigatório'),
              // paymentMethod: Yup.string().required(
              //   'O campo forma de pagamento é obrigatório',
              // ),
            }
          : {
              name: Yup.string().required('O campo nome é obrigatório'),
              whatsapp: Yup.string().required('O campo whatsapp é obrigatório'),
            },
      );

      await schema.validate(parseData, {
        abortEarly: false,
      });

      const orderTotal = parseFloat(`${parseData.subtotal}`);
      const minimumOrder = parseFloat(`${company.minimum_order}`);

      if (orderTotal < minimumOrder) {
        setLoading(false);
        setErrors({
          message: `O valor mínimo para realizar o pedido é de ${getCurrencyFormatted(
            minimumOrder,
          )}.`,
        });
        return;
      }

      // if (data.paymentMethod === '3') {
      //   const inputFile = document.getElementById(
      //     'pix_file',
      //   ) as HTMLInputElement;

      //   let image = '';

      //   if (inputFile && inputFile.files && inputFile.files.length > 0) {
      //     const file = inputFile.files[0];

      //     const storageRef = Storage.ref(`receipts/${v4()}.png`);
      //     const resposne = await storageRef.put(file);
      //     image = await resposne.ref.getDownloadURL();

      //     parseData = {
      //       ...parseData,
      //       pix_file: image,
      //     };
      //   } else {
      //     setLoading(false);
      //     alert('Por favor, envie o comprovante de transferência PIX.');
      //     setErrors({
      //       pix_file: 'O comprovante de PIX é obrigatório',
      //     });
      //     return;
      //   }
      // }

      await setDoc(doc(DB, 'orders', uid), parseData);

      if (company.fidelit && company.fidelit_quantity && !fidelitAlert.value) {
        const docClient = await getDoc(doc(DB, 'clients', user.uid));
        const client = docClient.data() as IClient;

        if (
          client.fidelit_order_quantity &&
          client.fidelit_order_quantity >= company.fidelit_quantity
        ) {
          setFidelitDiscount(0);
          await updateDoc(doc(DB, 'clients', user.uid), {
            fidelit_balance: 0,
            fidelit_order_quantity: 0,
          });
        } else {
          await updateDoc(doc(DB, 'clients', user.uid), {
            fidelit_balance:
              (client.fidelit_balance || 0) + (company.fidelit_value || 0),
            fidelit_order_quantity: (client.fidelit_order_quantity || 0) + 1,
          });
        }
      }

      if (coupon && coupon.uid) {
        const uuid = v4();
        await setDoc(doc(DB, 'used_coupons', uuid), {
          uid: uuid,
          order_id: uid,
          order_code: code,
          coupon_id: coupon.uid,
          coupon_code: coupon.code,
          phone: data.whatsapp,
          client: data.name,
          company_id: company.uid,
          company_user_id: company.user_id,
          created_at: new Date(),
        });
      }

      if (REACT_APP_USE_API_PRODUCTS === 'true') await loadProductsHeroku();
      else await loadProducts();

      await updateInventoryOfProductsSold();

      if (deliveryMethod === '1') {
        // atualizar endereço de entrega do cliente
        await updateDoc(doc(DB, 'clients', user.uid), {
          address: getAddressFormatted(address),
          orderAddress: address,
        });
      }

      // incrementar contador de pedidos do cliente
      await updateDoc(doc(DB, 'clients', user.uid), {
        order_count: currentOrderCount + 1,
      });

      await bff.delete(`/orders/${REACT_APP_FIREBASE_PROJECT}`);

      notify(parseData.name, parseData.total, uid);

      // const ownerCompanyEmail = await getEmailOwnerCompany(company.user_id);
      // if (ownerCompanyEmail) {
      //   await oneSignalNotify({
      //     email: ownerCompanyEmail,
      //     code: `#${code}`,
      //     username: data.name,
      //     valor: getCurrencyFormatted(total),
      //   });
      // }

      orderStatusRealtime(uid);

      updateReport();

      clearCart();

      // Rastrear evento de purchase no Analytics
      logPurchase(uid, total, 'BRL');
      logEvent('order_completed', {
        order_id: uid,
        order_code: code,
        total_value: total,
        payment_method: data.paymentMethod,
        delivery_method: deliveryMethod,
        company_name: company.fantasy,
        items_count: parseProducts.length,
      });

      // adicionar pedido a mesa
      if (table && table.uid) handleRegisterOrderInTable(parseData);

      let success_message =
        'Seu pedido foi realizado! Obrigado pela preferência.';

      if (company.whatsapp_order === '1') {
        success_message =
          company.order_finish_message ||
          'Seu pedido foi realizado! Confirme o pedido enviando para o nosso whatsapp.';
      }

      if (company.order_finish_message && deliveryMethod === '2') {
        // mesa
        success_message = company.order_finish_message;
      }

      Swal.fire({
        title: 'Parabéns!',
        text: success_message,
        icon: 'success',
        confirmButtonColor: company.app_color,
      });

      if (
        (data.paymentMethod === '1' || data.paymentMethod === '2') &&
        company.payment_url
      ) {
        setLoading(false);
        window.open(
          `${company.payment_url}?value=${getCurrencyFormatted(total)}`,
          '_blank',
        );
      }

      history.push(`/${company.app_domain}/orders${search}`);

      if (company.whatsapp_order === '1') {
        setTimeout(() => {
          parseData.paymentMethod = PAYMENT_METHODS[data.paymentMethod];

          const orderDetailsTxt = getOrderDetails(parseData);

          let parseCompanyPhone = company.phone.replace('(', '');
          parseCompanyPhone = parseCompanyPhone.replace(')', '');
          parseCompanyPhone = parseCompanyPhone.replace('-', '');
          parseCompanyPhone = parseCompanyPhone.replace(' ', '');

          const btn = document.querySelector('.swal2-confirm') as any;
          if (btn) {
            btn.onclick = () => {
              Swal.close();

              window.open(
                `https://api.whatsapp.com/send?phone=55${parseCompanyPhone}&text=${orderDetailsTxt}`,
                '_blank',
              );
            };
          }
        }, 100);
      }

      setLoading(false);

      // const link = document.createElement('a');
      // link.setAttribute('styles', 'display: none');
      // link.setAttribute('target', '_blank');
      // link.href = `https://api.whatsapp.com/send?phone=55${parseCompanyPhone}&text=${orderDetailsTxt}`;
      // document.body.appendChild(link);

      // setTimeout(() => link.click(), 3000);
    } catch (err) {
      // console.log(err);
      setLoading(false);
      // alert(JSON.stringify(err));
      if (err instanceof Yup.ValidationError) {
        const parseErrors = getValidationErrors(err);
        setErrors(parseErrors);
      }
    }
  }, [
    data,
    handleRegisterOrderInTable,
    deliveryMethod,
    address,
    parseProducts,
    updateInventoryOfProductsSold,
    shippingCost,
    subtotal,
    loadProducts,
    total,
    company,
    clearCart,
    notify,
    updateReport,
    history,
    orderStatusRealtime,
    user,
    table,
    loadProductsHeroku,
    search,
    coupon,
    fidelitDiscount,
    setFidelitDiscount,
    handleOrderTable,
    fidelitAlert,
  ]);

  const preHandleSubmit = useCallback(async () => {
    setLoading(true);
    await loadCompany();

    setTimeout(() => handleSubmit(), 100);
  }, [handleSubmit, loadCompany]);

  return (
    <App loading={false}>
      <div className="row">
        <div className="col-12 col-md-6 col-lg-4 mx-auto">
          <div className="card card-light shadow-sm mb-4">
            <div className="card-body">
              <form>
                {errors.message && (
                  <div className="alert alert-danger" role="alert">
                    {errors.message}
                  </div>
                )}
                <div className="form-floating mb-3 border rounded">
                  <input
                    type="text"
                    className="form-control"
                    id="pincode"
                    placeholder="Digite seu nome"
                    defaultValue={data.name}
                    onChange={event => {
                      setData({
                        ...data,
                        name: event.target.value,
                      });
                    }}
                  />
                  <label htmlFor="select">Qual o seu nome?</label>
                </div>
                {errors.name && (
                  <p className="invalid" style={{ marginTop: -14 }}>
                    {errors.name}
                  </p>
                )}
                <div className="form-floating mb-3 border rounded">
                  <input
                    type="text"
                    className="form-control"
                    id="pincode"
                    placeholder="Digite seu nome"
                    defaultValue={data.whatsapp}
                    onChange={event => {
                      setData({
                        ...data,
                        whatsapp: event.target.value,
                      });
                    }}
                  />
                  <label htmlFor="select">Número whatsapp</label>
                </div>
                {errors.whatsapp && (
                  <p className="invalid" style={{ marginTop: -14 }}>
                    {errors.whatsapp}
                  </p>
                )}

                {!table.uid && (
                  <>
                    <div className="form-floating mb-3 border rounded">
                      <select
                        className="form-select"
                        id="select"
                        style={{ opacity: 1 }}
                        defaultValue={data.paymentMethod}
                        onChange={event => {
                          const paymentMethod = event.target.value;
                          setData({
                            ...data,
                            paymentMethod,
                            thing: paymentMethod === '0' ? data.thing : '',
                          });
                        }}
                      >
                        <option value="0">Dinheiro</option>
                        <option value="1">Débito</option>
                        <option value="2">Crédito</option>
                        {company.pix_active === '1' && (
                          <option value="3">PIX</option>
                        )}
                      </select>
                      <label htmlFor="select">Forma de pagamento</label>
                    </div>
                    {(data.paymentMethod === '1' ||
                      data.paymentMethod === '2') &&
                    company.payment_url ? (
                      <>
                        <p className="mb-5 text-center">
                          {`Ao finalizar o pedido você será redirecionado para o link de pagamento,
                          informe o valor total do pedido: ${getCurrencyFormatted(
                            total,
                          )}`}{' '}
                        </p>
                      </>
                    ) : (
                      <></>
                    )}

                    {data.paymentMethod === '2' && company.add_card > 0 && (
                      <p className="mb-3">{messageAddCard}</p>
                    )}
                    {data.paymentMethod === '1' && company.add_card2 > 0 && (
                      <p className="mb-3">{messageAddCard}</p>
                    )}
                    {data.paymentMethod === '0' && (
                      <div className="form-floating mb-3 border rounded">
                        <input
                          type="number"
                          className="form-control"
                          id="pincode"
                          placeholder="Digite seu nome"
                          onChange={event => {
                            setData({
                              ...data,
                              thing: event.target.value,
                            });
                          }}
                        />
                        <label htmlFor="select">Troco para?</label>
                      </div>
                    )}
                    {errors.thing && (
                      <p
                        className="invalid"
                        style={{ marginTop: -14, marginBottom: 12 }}
                      >
                        {errors.thing}
                      </p>
                    )}
                  </>
                )}

                {data.paymentMethod === '3' && (
                  <>
                    <div
                      className="col-sm-12 border rounded mb-3"
                      style={{
                        padding: '1rem',
                        fontSize: 14,
                        position: 'relative',
                      }}
                    >
                      <p className="mb-0">Chave PIX</p>
                      <p id="pix" className="fw-bold mb-2">
                        {company.pix}
                      </p>

                      <p className="mb-0">Tipo de chave</p>
                      <p className="fw-bold mb-2">{company.pix_type}</p>

                      <p className="mb-0">Beneficiário</p>
                      <p className="fw-bold mb-2">{company.pix_recipient}</p>

                      <p className="mb-0">Valor</p>
                      <p className="fw-bold mb-0">
                        {getCurrencyFormatted(total)}
                      </p>

                      <button
                        type="button"
                        style={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          border: 0,
                          paddingLeft: 8,
                          paddingRight: 8,
                          paddingTop: 4,
                          paddingBottom: 4,
                          borderRadius: 8,
                        }}
                        onClick={async () => {
                          await navigator.clipboard.writeText(company.pix);
                          alert('Chave Pix Copiada!');
                        }}
                      >
                        Copiar Chave
                      </button>
                    </div>

                    <div
                      className="col-sm-12 border rounded mb-3"
                      style={{
                        padding: '1rem',
                        fontSize: 14,
                        position: 'relative',
                      }}
                    >
                      <p className="mb-0 fw-bold">
                        Enviar Comprovante (opcional)
                      </p>
                      <input
                        style={{ width: '100%' }}
                        type="file"
                        id="pix_file"
                      />
                      {errors.pix_file && (
                        <p className="invalid mt-1">{errors.pix_file}</p>
                      )}
                    </div>
                  </>
                )}

                {/* <div className="row mb-3">
                  <div className="col-auto pr-0 align-self-center text-end">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="settingscheck1"
                        defaultChecked={remember}
                      />
                      <label
                        id="remember_input"
                        className="form-check-label"
                        htmlFor="settingscheck1"
                        onClick={() => setRemember(!remember)}
                      />
                    </div>
                  </div>
                  <div className="col ps-0">
                    <p
                      className="text-opac small d-block mt-1"
                      onClick={() =>
                        document.getElementById('remember_input')?.click()
                      }
                    >
                      Lembrar meus dados para próxima compra?
                    </p>
                  </div>
                </div> */}
              </form>
              {company.opened && (
                <div className="d-grid">
                  <button
                    type="button"
                    className="btn btn-lg btn-default shadow-sm"
                    onClick={preHandleSubmit}
                    disabled={loading}
                    style={loading ? { lineHeight: 0 } : {}}
                  >
                    {!loading && 'Finalizar Pedido'}
                    {loading && (
                      <div
                        className="spinner-border spinner-border-sm"
                        role="status"
                      >
                        <span className="sr-only" />
                      </div>
                    )}
                  </button>
                </div>
              )}
              {!company.opened && (
                <div className="d-flex justify-content-center">
                  Que pena! Estamos fechados : (
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </App>
  );
};

export default Checkout;
